import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Input, message, Modal, Select, Tag } from "antd";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { ChangeStatus, getDashboardEvents } from "../../services/ApiService";
import { hasPermission } from "../GlobalFunction";
import { statusChoices as sharedStatusChoices } from "../../utils/statusRules";


const CLOSING_REASONS = [
  "Resignation",
  "Redundancy",
  "Poor Performance",
  "Transfer of Employment",
  "Restructuring",
  "Fixed Term Contract End",
].map((r) => ({ label: r, value: r }));

const FILTERS = [
  { key: "all", label: "All" },
  { key: "onboard", label: "Onboard" },
  { key: "deboard", label: "Deboard" },
  { key: "overdue", label: "Overdue" },
];

// Rows are loaded 20 at a time; reaching the bottom of the list loads the next 20.
const PAGE_SIZE = 20;

const REPORT_TYPES = [
  { key: "all", label: "All (overdue + next 30 days + future)" },
  { key: "overdue", label: "Overdue only" },
  { key: "current", label: "Current (next 30 days)" },
  { key: "future", label: "Future (after 30 days)" },
];

const CATEGORY_LABEL = {
  overdue: "Overdue",
  current: "Current (next 30 days)",
  future: "Future",
};

const relativeText = (e) => {
  const d = e.days_delta;
  if (d === 0) return "Today";
  const n = Math.abs(d);
  const unit = n === 1 ? "day" : "days";
  return d > 0 ? `in ${n} ${unit}` : `${n} ${unit} late`;
};

const formatDate = (iso) => (iso ? dayjs(iso).format("DD-MMM-YYYY") : "");

// The popup is opened for an event row: its dates are ISO strings from the dashboard API.
const statusChoices = (event) =>
  sharedStatusChoices({
    status: event.status,
    startDate: event.start_date,
    endDate: event.end_date,
  });

const UpcomingEvents = ({ onChanged, onCounts }) => {
  const canUpdate = hasPermission("directory", "update");
  const [filter, setFilter] = useState("all");
  const [target, setTarget] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ------------------------------------------------------------ list + paging
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ all: 0, onboard: 0, deboard: 0, overdue: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const listRef = useRef(null);
  const latestRequest = useRef(0);
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;

  // mode: "reset" (first load / filter change), "more" (append the next page),
  //       "refresh" (re-read what is already on screen, e.g. after a status change)
  const fetchPage = useCallback(async ({ filterKey, offset, limit, mode }) => {
    const id = ++latestRequest.current;       // a newer request makes older answers irrelevant
    if (mode === "reset") setLoadingFirst(true);
    if (mode === "more") setLoadingMore(true);
    if (mode === "refresh") setRefreshing(true);
    try {
      const res = await getDashboardEvents(30, false, { limit, offset, filter: filterKey });
      if (id !== latestRequest.current || res?.responseCode !== 200) return;
      const rows = res.events || [];
      setItems((prev) => (mode === "more" ? [...prev, ...rows] : rows));
      setHasMore(!!res.has_more);
      if (res.summary) setSummary(res.summary);
      if (onCountsRef.current) onCountsRef.current(res.counts || {});
    } catch (error) {
      console.error("Error fetching upcoming & overdue events:", error);
    } finally {
      if (id === latestRequest.current) {
        setLoadingFirst(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    fetchPage({ filterKey: filter, offset: 0, limit: PAGE_SIZE, mode: "reset" });
  }, [filter, fetchPage]);

  const loadMore = () => {
    if (!hasMore || loadingMore || loadingFirst || refreshing) return;
    fetchPage({ filterKey: filter, offset: items.length, limit: PAGE_SIZE, mode: "more" });
  };

  const onListScroll = (ev) => {
    const el = ev.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) loadMore();
  };

  // After a status change only this list reloads (the rows already on screen, in one go).
  const refresh = () =>
    fetchPage({
      filterKey: filter,
      offset: 0,
      limit: Math.min(Math.max(items.length, PAGE_SIZE), 500),
      mode: "refresh",
    });

  // ------------------------------------------------------------ status update
  const openUpdate = (event) => {
    const { options } = statusChoices(event);
    setTarget(event);
    setNewStatus(options.length === 1 ? options[0].value : null);
    setEndDate(event.end_date ? new Date(event.end_date) : null);
    setReason(event.reason_of_closing || null);
    setNotes(event.termination_notes || "");
  };

  const closeUpdate = () => {
    if (saving) return;
    setTarget(null);
    setNewStatus(null);
    setEndDate(null);
    setReason(null);
    setNotes("");
  };

  const confirmUpdate = async () => {
    if (!target || !newStatus || newStatus === target.status) return;
    if (newStatus === "Exit" && !endDate) {
      message.error("Please select the End Date of Employment");
      return;
    }
    setSaving(true);
    try {
      const body = { onboard_id: target.onboard_form_id, status: newStatus };
      if (newStatus === "Exit") {
        body["29"] = dayjs(endDate).format("YYYY-MM-DD");
        body["72"] = reason || "";
        body["73"] = notes || "";
      }
      const res = await ChangeStatus(body);
      if (res?.responseCode === 200 || res?.message === "Employee Status updated successfully") {
        message.success(`Status of ${target.name} changed to ${newStatus}`);
        setSaving(false);
        setTarget(null);
        setNewStatus(null);
        refresh();
        onChanged && onChanged();
      } else {
        message.error(res?.message || res?.responseMessage || "Failed to update status");
        setSaving(false);
      }
    } catch (error) {
      console.error("Status update failed:", error);
      message.error("Error updating status");
      setSaving(false);
    }
  };

  // ------------------------------------------------------------- Excel report
  const downloadReport = async (type) => {
    setDownloading(true);
    try {
      const res = await getDashboardEvents(30, true);
      if (res?.responseCode !== 200) {
        message.error(res?.responseMessage || "Failed to prepare the report");
        return;
      }
      const rows = (res.events || []).filter((e) => type === "all" || e.category === type);
      if (rows.length === 0) {
        message.info("Nothing to report for this selection.");
        return;
      }
      const sheetRows = rows.map((e) => ({
        Category: CATEGORY_LABEL[e.category] || e.category,
        Event: e.event === "onboard" ? "Onboarding" : "Deboarding",
        Date: formatDate(e.date),
        "Days": relativeText(e),
        "Employee ID": e.employee_id,
        "Employee Name": e.name,
        Client: e.client,
        Customer: e.customer,
        Country: e.country,
        "Account Owner": e.owner,
        Status: e.status,
        "Start Date": formatDate(e.start_date),
        "End Date": formatDate(e.end_date),
      }));
      const sheet = XLSX.utils.json_to_sheet(sheetRows);
      const headers = Object.keys(sheetRows[0]);
      sheet["!cols"] = headers.map((h) => ({
        wch: Math.min(
          Math.max(h.length, ...sheetRows.map((r) => String(r[h] ?? "").length)) + 2,
          45
        ),
      }));
      headers.forEach((_, c) => {
        const cell = sheet[XLSX.utils.encode_cell({ r: 0, c })];
        if (cell) {
          cell.s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "4F81BD" } },
            alignment: { horizontal: "center" },
          };
        }
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Upcoming & Overdue");
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Upcoming_Overdue_${type}_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch (error) {
      console.error("Report download failed:", error);
      message.error("Failed to download the report");
    } finally {
      setDownloading(false);
    }
  };

  const choices = target ? statusChoices(target) : { options: [], note: "" };
  const unchanged = !target || !newStatus || newStatus === target.status;
  const exitNeedsDate = newStatus === "Exit" && !endDate;

  return (
    <div className="upcoming-events">
      <div className="upcoming-events-toolbar">
        <div className="upcoming-events-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`upcoming-filter-chip${filter === f.key ? " active" : ""}${
                f.key === "overdue" ? " overdue" : ""
              }`}
              onClick={() => setFilter(f.key)}
            >
              {f.label} <span className="upcoming-filter-count">{summary[f.key]}</span>
            </button>
          ))}
        </div>
        <Dropdown
          trigger={["click"]}
          menu={{
            items: REPORT_TYPES.map((r) => ({ key: r.key, label: r.label })),
            onClick: ({ key }) => downloadReport(key),
          }}
        >
          <Button size="small" loading={downloading} icon={<DownloadOutlined />}>
            Report <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      {loadingFirst ? (
        <div className="activity-feed-empty">Loading...</div>
      ) : items.length === 0 ? (
        <div className="activity-feed-empty">
          Nothing due or overdue for this selection.
        </div>
      ) : (
        <div
          className="activity-feed upcoming-events-list"
          ref={listRef}
          onScroll={onListScroll}
          style={refreshing ? { opacity: 0.45, pointerEvents: "none", transition: "opacity 0.2s" } : undefined}
        >
          {items.map((e) => (
            <div
              className="activity-feed-row"
              key={`${e.onboard_form_id}-${e.event}`}
            >
              <div className="activity-feed-main">
                <span className="activity-feed-name">
                  <Tag color={e.event === "onboard" ? "blue" : "orange"} style={{ marginRight: 6 }}>
                    {e.event === "onboard" ? "Onboard" : "Deboard"}
                  </Tag>
                  {e.name || "N/A"}
                </span>
                <span className="activity-feed-by">
                  {[e.client, e.customer].filter(Boolean).join(" · ")}
                </span>
              </div>
              <div className="upcoming-events-side">
                <span className={`upcoming-events-when${e.category === "overdue" ? " overdue" : ""}`}>
                  {formatDate(e.date)}
                  <small>{relativeText(e)}</small>
                </span>
                <Tag>{e.status}</Tag>
                {canUpdate && (
                  <Button size="small" onClick={() => openUpdate(e)}>
                    Update Status
                  </Button>
                )}
              </div>
            </div>
          ))}
          {loadingMore && <div className="upcoming-events-more">Loading more...</div>}
        </div>
      )}

      <Modal
        title="Confirm Status Change"
        open={!!target}
        onCancel={closeUpdate}
        onOk={confirmUpdate}
        okText="Yes, Change"
        cancelText="Cancel"
        confirmLoading={saving}
        okButtonProps={{ disabled: unchanged || exitNeedsDate }}
        centered
        maskClosable={false}
      >
        {target && (
          <>
            <p style={{ marginBottom: 12 }}>
              <strong>{target.name}</strong>
              {target.client ? ` (${target.client})` : ""}
              <br />
              Current status: <Tag>{target.status}</Tag>
            </p>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
              Change status to
            </label>
            <Select
              placeholder="Select the new status"
              value={newStatus || undefined}
              onChange={setNewStatus}
              options={choices.options}
              style={{ width: "100%" }}
            />
            {choices.note && (
              <small style={{ display: "block", marginTop: 6, color: "#8c6d1f" }}>
                {choices.note}
              </small>
            )}

            {newStatus === "Exit" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    End Date of Employment <span style={{ color: "red" }}>*</span>
                  </label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select end date"
                    className="exit-date-input"
                    wrapperClassName="exit-date-wrapper"
                    isClearable
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    Reason of Closing
                  </label>
                  <Select
                    placeholder="Select reason"
                    value={reason || undefined}
                    onChange={setReason}
                    options={CLOSING_REASONS}
                    allowClear
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                    Termination Notes
                  </label>
                  <Input.TextArea
                    rows={3}
                    placeholder="Add any termination notes"
                    value={notes}
                    onChange={(ev) => setNotes(ev.target.value)}
                  />
                </div>
              </div>
            )}

            {!unchanged && (
              <p className="upcoming-events-confirm">
                Are you sure you want to change the status of{" "}
                <strong>{target.name}</strong> from <strong>{target.status}</strong> to{" "}
                <strong>{newStatus}</strong>?
                <br />
                <small>This change is recorded in the activity log.</small>
              </p>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default UpcomingEvents;
